import { getApp, getApps, initializeApp } from "firebase/app";
import {
  confirmPasswordReset,
  connectAuthEmulator,
  EmailAuthProvider,
  getAuth,
  getMultiFactorResolver,
  multiFactor,
  type MultiFactorError,
  type MultiFactorResolver,
  OAuthProvider,
  reauthenticateWithCredential,
  reload,
  sendPasswordResetEmail as sendFirebasePasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  TotpMultiFactorGenerator,
  type TotpSecret,
  verifyPasswordResetCode,
} from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { connectStorageEmulator, getStorage } from "firebase/storage";

const useEmulators =
  import.meta.env.VITE_USE_EMULATORS !== "false" && import.meta.env.DEV;
const microsoftTenantId = import.meta.env.VITE_MS_TENANT_ID?.trim();
const browserEmulatorHost =
  typeof window === "undefined" || !window.location.hostname
    ? "127.0.0.1"
    : window.location.hostname;
const authEmulatorHost = browserEmulatorHost.includes(":")
  ? `[${browserEmulatorHost}]`
  : browserEmulatorHost;

function cloudConfigValue(name: string, value: string | undefined) {
  const configured = value?.trim();
  if (!configured) {
    throw new Error(
      `Firebase cloud configuration is incomplete: ${name} is required.`,
    );
  }
  return configured;
}

const firebaseConfig = useEmulators
  ? {
      apiKey: "demo-project-planner-key",
      authDomain: "project-planner-local.firebaseapp.com",
      projectId: "project-planner-local",
      storageBucket: "project-planner-local.appspot.com",
      messagingSenderId: "000000000000",
      appId: "1:000000000000:web:project-planner-local",
    }
  : {
      apiKey: cloudConfigValue(
        "VITE_FIREBASE_API_KEY",
        import.meta.env.VITE_FIREBASE_API_KEY,
      ),
      authDomain: cloudConfigValue(
        "VITE_FIREBASE_AUTH_DOMAIN",
        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      ),
      projectId: cloudConfigValue(
        "VITE_FIREBASE_PROJECT_ID",
        import.meta.env.VITE_FIREBASE_PROJECT_ID,
      ),
      storageBucket: cloudConfigValue(
        "VITE_FIREBASE_STORAGE_BUCKET",
        import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      ),
      messagingSenderId: cloudConfigValue(
        "VITE_FIREBASE_MESSAGING_SENDER_ID",
        import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      ),
      appId: cloudConfigValue(
        "VITE_FIREBASE_APP_ID",
        import.meta.env.VITE_FIREBASE_APP_ID,
      ),
    };

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const firestore = getFirestore(app);
export const storage = getStorage(app);

if (
  useEmulators &&
  !(globalThis as typeof globalThis & { __plannerAuthConnected?: boolean })
    .__plannerAuthConnected
) {
  connectAuthEmulator(
    auth,
    import.meta.env.VITE_AUTH_EMULATOR_HOST ||
      `http://${authEmulatorHost}:9099`,
    { disableWarnings: true },
  );
  (
    globalThis as typeof globalThis & { __plannerAuthConnected?: boolean }
  ).__plannerAuthConnected = true;
}

if (
  useEmulators &&
  !(globalThis as typeof globalThis & { __plannerStorageConnected?: boolean })
    .__plannerStorageConnected
) {
  connectStorageEmulator(
    storage,
    import.meta.env.VITE_STORAGE_EMULATOR_HOST || browserEmulatorHost,
    Number(import.meta.env.VITE_STORAGE_EMULATOR_PORT || 9199),
  );
  (
    globalThis as typeof globalThis & {
      __plannerStorageConnected?: boolean;
    }
  ).__plannerStorageConnected = true;
}

if (
  useEmulators &&
  !(globalThis as typeof globalThis & { __plannerFirestoreConnected?: boolean })
    .__plannerFirestoreConnected
) {
  connectFirestoreEmulator(
    firestore,
    import.meta.env.VITE_FIRESTORE_EMULATOR_HOST || browserEmulatorHost,
    Number(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT || 8085),
  );
  (
    globalThis as typeof globalThis & {
      __plannerFirestoreConnected?: boolean;
    }
  ).__plannerFirestoreConnected = true;
}

export async function signInDemo(kind: "internal" | "admin" | "client") {
  const credentials =
    kind === "internal"
      ? { email: "owner@vipdesk.local", password: "PlannerDemo!2026" }
      : kind === "admin"
        ? { email: "admin@vipdesk.local", password: "PlannerDemo!2026" }
        : { email: "client@thorne.local", password: "PlannerDemo!2026" };
  return signInWithEmailAndPassword(
    auth,
    credentials.email,
    credentials.password,
  );
}

export async function signInMicrosoft() {
  if (!useEmulators && !microsoftTenantId) {
    throw Object.assign(
      new Error(
        "Microsoft sign-in requires the approved VIPdesk Entra tenant ID.",
      ),
      { code: "planner/microsoft-tenant-missing" },
    );
  }
  const provider = new OAuthProvider("microsoft.com");
  provider.addScope("User.Read");
  provider.setCustomParameters({
    prompt: "select_account",
    ...(microsoftTenantId ? { tenant: microsoftTenantId } : {}),
  });
  const result = await signInWithPopup(auth, provider);
  const credential = OAuthProvider.credentialFromResult(result);
  if (!credential?.idToken || !credential.accessToken) {
    throw Object.assign(
      new Error("Microsoft sign-in did not return identity verification."),
      { code: "planner/microsoft-verification-missing" },
    );
  }
  return {
    microsoftAccessToken: credential.accessToken,
    microsoftIdToken: credential.idToken,
  };
}

export interface ExternalMfaFactor {
  uid: string;
  displayName: string;
}

export interface ExternalMfaChallenge {
  resolver: MultiFactorResolver;
  factors: ExternalMfaFactor[];
}

export type ExternalSignInResult =
  | { status: "signed-in" }
  | { status: "mfa-enrollment-required" }
  | { status: "mfa-required"; challenge: ExternalMfaChallenge };

export type ExternalMfaSessionStatus =
  | "not-external"
  | "verified"
  | "mfa-enrollment-required"
  | "mfa-sign-in-required";

export interface ExternalTotpEnrollment {
  secret: TotpSecret;
  secretKey: string;
  authenticatorUri: string;
}

function authErrorCode(error: unknown) {
  return typeof error === "object" && error && "code" in error
    ? String(error.code)
    : "";
}

function sixDigitCode(value: string) {
  const code = value.replace(/\s/g, "");
  if (!/^\d{6}$/.test(code)) {
    throw Object.assign(new Error("Enter a 6-digit verification code."), {
      code: "planner/invalid-totp-code",
    });
  }
  return code;
}

function currentExternalUser() {
  const user = auth.currentUser;
  const providerIds = user?.providerData.map((provider) => provider.providerId);
  if (
    !user ||
    !providerIds?.includes("password") ||
    providerIds.includes("microsoft.com")
  ) {
    throw Object.assign(
      new Error(
        "Authenticator-app security is available to external accounts.",
      ),
      { code: "planner/external-account-required" },
    );
  }
  return user;
}

export async function signInExternal(
  email: string,
  password: string,
): Promise<ExternalSignInResult> {
  try {
    await signInWithEmailAndPassword(
      auth,
      email.trim().toLowerCase(),
      password,
    );
    if (!useEmulators && externalTotpFactors().length === 0) {
      return { status: "mfa-enrollment-required" };
    }
    return { status: "signed-in" };
  } catch (error) {
    if (authErrorCode(error) !== "auth/multi-factor-auth-required") throw error;
    const resolver = getMultiFactorResolver(auth, error as MultiFactorError);
    const factors = resolver.hints
      .filter(
        (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
      )
      .map((factor, index) => ({
        uid: factor.uid,
        displayName: factor.displayName || `Authenticator app ${index + 1}`,
      }));
    if (!factors.length) {
      throw Object.assign(
        new Error("This account uses an unsupported verification method."),
        { code: "planner/unsupported-mfa-factor" },
      );
    }
    return {
      status: "mfa-required",
      challenge: { resolver, factors },
    };
  }
}

export async function externalMfaSessionStatus(): Promise<ExternalMfaSessionStatus> {
  const user = auth.currentUser;
  const providerIds = user?.providerData.map((provider) => provider.providerId);
  if (
    !user ||
    !providerIds?.includes("password") ||
    providerIds.includes("microsoft.com")
  ) {
    return "not-external";
  }
  if (useEmulators) return "verified";
  const token = await user.getIdTokenResult();
  const firebaseClaims = token.claims.firebase as
    { sign_in_second_factor?: unknown } | undefined;
  if (firebaseClaims?.sign_in_second_factor === "totp") return "verified";
  return externalTotpFactors().length
    ? "mfa-sign-in-required"
    : "mfa-enrollment-required";
}

export async function completeExternalMfaSignIn(
  challenge: ExternalMfaChallenge,
  factorUid: string,
  verificationCode: string,
) {
  if (!challenge.factors.some((factor) => factor.uid === factorUid)) {
    throw Object.assign(new Error("Choose an available authenticator app."), {
      code: "planner/invalid-mfa-factor",
    });
  }
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(
    factorUid,
    sixDigitCode(verificationCode),
  );
  await challenge.resolver.resolveSignIn(assertion);
}

export function externalTotpFactors(): ExternalMfaFactor[] {
  const user = currentExternalUser();
  return multiFactor(user)
    .enrolledFactors.filter(
      (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    )
    .map((factor, index) => ({
      uid: factor.uid,
      displayName: factor.displayName || `Authenticator app ${index + 1}`,
    }));
}

export async function startExternalTotpEnrollment(
  currentPassword: string,
): Promise<ExternalTotpEnrollment> {
  const user = currentExternalUser();
  if (!user.emailVerified) {
    throw Object.assign(new Error("Verify your email before adding MFA."), {
      code: "auth/unverified-email",
    });
  }
  if (externalTotpFactors().length) {
    throw Object.assign(new Error("An authenticator app is already enabled."), {
      code: "planner/mfa-already-enabled",
    });
  }
  if (!user.email) {
    throw Object.assign(new Error("This account does not have an email."), {
      code: "planner/account-email-required",
    });
  }
  await reauthenticateWithCredential(
    user,
    EmailAuthProvider.credential(user.email, currentPassword),
  );
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  return {
    secret,
    secretKey: secret.secretKey,
    authenticatorUri: secret.generateQrCodeUrl(
      user.email,
      "VIPdesk Project Planner",
    ),
  };
}

export async function finishExternalTotpEnrollment(
  enrollment: ExternalTotpEnrollment,
  verificationCode: string,
) {
  const user = currentExternalUser();
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
    enrollment.secret,
    sixDigitCode(verificationCode),
  );
  await multiFactor(user).enroll(assertion, "Authenticator app");
  await user.getIdToken(true);
}

export async function refreshExternalIdentity() {
  const user = currentExternalUser();
  await reload(user);
  await user.getIdToken(true);
}

export function verifyAccountSetupCode(actionCode: string) {
  return verifyPasswordResetCode(auth, actionCode);
}

export function completeAccountSetup(actionCode: string, password: string) {
  return confirmPasswordReset(auth, actionCode, password);
}

export async function requestPasswordReset(email: string) {
  await sendFirebasePasswordResetEmail(auth, email.trim().toLowerCase(), {
    url: `${window.location.origin}/login`,
  });
}

export async function refreshAuthToken() {
  return auth.currentUser?.getIdToken(true) ?? null;
}

export function signOutUser() {
  return signOut(auth);
}

export async function authToken() {
  return auth.currentUser?.getIdToken() ?? null;
}

export { useEmulators };
